import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';

import { TooltipModule } from 'primeng/tooltip';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import {
  MedicalICD9Response,
  MedicalResponse,
} from '../../../../models/api/view/claim/details/medical/medical-response';
import {
  EffectiveDate,
  ICDCode,
  MedicalUIData,
  MedicalUIModelFactory,
  ReturnToWorkDay,
} from '../../../../models/api/view/claim/details/medical/medical-ui-models';
import { TimelineCard } from '../../../../models/api/view/claim/summary/timelinecard';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { BaseComponent } from '../../../base/base.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { IconComponent } from '../../../core/labels/icon.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { LoadingPanelComponent } from '../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-icd',
  imports: [
    TableComponent,
    ColumnComponent,
    SelectComponent,
    TooltipModule,
    ActionRowComponent,
    LinkLabelComponent,
    DetailPanelComponent,
    DetailItemComponent,
    LoadingPanelComponent,
    IconComponent,
  ],
  providers: [FormatPipe],
  template: `
    <app-loading-panel
      loadingStyle="Box"
      [loadingHeight]="500"
      [state]="loading ? 'Loading' : 'Loaded'"
      [error]="error"
    >
      <ng-template #loaded>
        <app-action-row>
          <div left1>
            <div
              class="tw-font-bold tw-flex tw-items-center tw-gap-4 tw-pb-4 tw-pt-2"
            >
              <div class="  ">Current work status</div>
              <div
                class="tw-flex tw-items-center tw-font-bold tw-pl-2 tw-pr-3 tw-py-1 tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-border"
              >
                <div
                  class="tw-rounded-full tw-size-6 tw-bg-[var(--sed-purple-40-color)] tw-flex tw-items-center tw-justify-center tw-mr-1"
                >
                  @if (workStatus === 'Working') {
                    <app-icon name="fa-regular fa-check" size="12px"></app-icon>
                  } @else if (workStatus === 'Off work') {
                    <app-icon name="fa-regular fa-xmark" size="12px"></app-icon>
                  } @else if (workStatus === 'Working restricted') {
                    <app-icon name="fa-regular fa-clock" size="12px"></app-icon>
                  }
                </div>
                <span> {{ workStatus }}</span>
              </div>
              <div class="tw-pl-8 ">Days off work</div>

              <div
                class="tw-font-bold tw-center tw-flex tw-flex-col tw-items-center"
              >
                <div
                  class="tw-px-3 tw-py-1 tw-rounded-lg tw-text-center tw-w-full"
                  [style]="{
                    'background-color':
                      labelDayIndex >= 6
                        ? 'color-mix(in srgb,var(--sed-status-red-color),#FFF 90%)'
                        : labelDayIndex >= 4
                          ? '#FFF4DC'
                          : 'color-mix(in srgb,var(--sed-status-green-color),#FFF 90%)',
                  }"
                >
                  <app-link-label
                    (click)="view.navigateCurrentClaim('time-tracking')"
                    [classes]="
                      labelDayIndex >= 6
                        ? 'tw-text-[var(--sed-status-red-color)]'
                        : labelDayIndex >= 4
                          ? 'tw-text-[#B07B00]'
                          : 'tw-text-[var(--sed-status-green-color)]'
                    "
                  >
                    @if (offWorkDays === 0 || !offWorkDays) {
                      0 days
                    } @else {
                      {{ offWorkDays }} days since
                      {{ formattedFirstAbsenceDate }}
                    }
                  </app-link-label>
                </div>
              </div>
            </div>
          </div>
        </app-action-row>
        <hr />
        <div class="tw-overflow-x-auto max-md:tw-overflow-x-visible">
          <div
            class="tw-flex tw-flex-col tw-items-center tw-min-w-[1000px] max-md:tw-min-w-0"
          >
            <div
              class="tw-flex tw-justify-between tw-w-[1000px] tw-pb-8 max-md:tw-flex-col max-md:tw-w-full max-md:tw-gap-4"
            >
              <div
                class="tw-font-bold tw-center tw-flex tw-flex-col tw-items-start tw-pt-6 tw-pl-2 "
              >
                <h3
                  class="tw-text-sm tw-m-0 tw-text-[var(--sed-black-60-color)] tw-pt-2"
                >
                  Estimated days away from work
                </h3>
                <div class="tw-text-xl tw-py-2">
                  @if (
                    currentData?.primaryICD?.code &&
                    currentData?.primaryICD?.group
                  ) {
                    {{ currentData.primaryICD.code }} -
                    {{ currentData.primaryICD.group }}
                  } @else if (currentData?.primaryICD?.code) {
                    {{ currentData.primaryICD.code }}
                  } @else if (currentData?.primaryICD?.group) {
                    {{ currentData.primaryICD.group }}
                  }
                </div>
              </div>

              <div class="tw-flex tw-items-center tw-w-64 max-md:tw-w-full">
                <div class="tw-grow">
                  <app-select
                    [options]="effectiveDates"
                    optionLabel="description"
                    optionValue="value"
                    class="tw-basis-1/5"
                    [virtualScroll]="false"
                    [control]="effectiveDate"
                    label="As of"
                    [multiple]="false"
                    [filter]="false"
                    [showClear]="false"
                    [showHelpText]="false"
                  />
                </div>
              </div>
            </div>
            <div class="md:tw-hidden tw-w-full tw-pb-5">
              @for (category of riskCategories; track category.key) {
                <div class="tw-mb-6">
                  <div
                    class="tw-border tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-py-2 tw-text-center tw-font-bold tw-text-[var(--sed-black-80-color)] tw-mb-3"
                  >
                    {{ category.label }}
                  </div>
                  <div class="tw-flex tw-flex-col tw-items-center tw-gap-3">
                    @for (
                      day of getReturnToWorkDaysByCategory(category.key);
                      track day.label;
                      let idx = $index
                    ) {
                      <div
                        [id]="'alert-mobile-' + category.key + '-' + idx"
                        aria-live="polite"
                        class="screen-reader-text"
                      >
                        {{ day.label }} of people return to work within
                        {{ day.value }} days
                      </div>
                      <div
                        [pTooltip]="
                          day.label +
                          ' of people return to work within ' +
                          day.value +
                          ' days'
                        "
                        tooltipPosition="top"
                        class="tw-flex tw-border tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-p-2 tw-flex-col tw-w-[100px] tw-items-center tw-text-center"
                        role="caption"
                        [attr.aria-labelledby]="
                          'alert-mobile-' + category.key + '-' + idx
                        "
                      >
                        <div class="tw-relative tw-w-[60px]">
                          <div
                            class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-[var(--sed-black-80-color)] tw-font-bold"
                          >
                            {{ day.label }}
                          </div>
                          <div
                            class="arc tw-mt-3"
                            [style]="{
                              '--a': (day.percentage / 10) * 36 + 'deg',
                              'background-color':
                                day.color === 'red'
                                  ? 'var(--sed-status-red-color)'
                                  : day.color === 'yellow'
                                    ? 'var(--sed-status-yellow-color)'
                                    : 'var(--sed-status-green-color)',
                            }"
                          ></div>
                        </div>

                        <div class="tw-text-xl tw-font-bold tw-pt-3">
                          {{ day.value }}
                        </div>
                        <div class="tw-text-sm tw-pb-4">days</div>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
            <div class="tw-hidden md:tw-contents">
              <div
                class="tw-flex tw-gap-3 tw-pb-4 tw-text-center tw-w-900px] tw-font-bold tw-text-[var(--sed-black-80-color)]  "
              >
                <div
                  class="tw-w-[432px] tw-py-2
          tw-border tw-rounded-lg  tw-bg-[var(--sed-open-grey-color)] "
                >
                  Low-risk
                </div>
                <div
                  class="tw-w-[210px] tw-border tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-py-2"
                >
                  Mid-range
                </div>
                <div
                  class="tw-w-[320px]  tw-border tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-py-2 "
                >
                  At-risk
                </div>
              </div>
              <div class="tw-flex tw-text-center tw-gap-3 tw-flex-wrap tw-pb-5">
                @for (
                  day of currentData?.returnToWorkDays || [];
                  track day.label;
                  let idx = $index
                ) {
                  <div
                    [id]="'alert-' + idx"
                    aria-live="polite"
                    class="screen-reader-text"
                  >
                    {{ day.label }} of people return to work within
                    {{ day.value }} days
                  </div>
                  <div
                    [pTooltip]="
                      day.label +
                      ' of people return to work within ' +
                      day.value +
                      ' days'
                    "
                    tooltipPosition="top"
                    class="tw-flex tw-border tw-rounded-lg tw-bg-[var(--sed-open-grey-color)] tw-p-2 tw-flex-col tw-w-[100px]"
                    role="caption"
                    [attr.aria-labelledby]="'alert-' + idx"
                  >
                    <div style="position: relative;">
                      <div
                        class="tw-text-[var(--sed-black-80-color)] tw-font-bold"
                        style="position:absolute;width: 100%; height: 100%;"
                      >
                        <div class="tw-mt-[30px]">{{ day.label }}</div>
                      </div>
                      <div
                        class="arc tw-ml-[12px] tw-mt-3"
                        [style]="{
                          '--a': (idx + 1) * 36 + 'deg',
                          'background-color':
                            idx >= 6
                              ? 'var(--sed-status-red-color)'
                              : idx >= 4
                                ? 'var(--sed-status-yellow-color)'
                                : 'var(--sed-status-green-color)',
                        }"
                      ></div>
                    </div>

                    <div class="tw-text-xl tw-font-bold tw-pt-3">
                      {{ day.value }}
                    </div>
                    <div class="tw-text-sm tw-pb-4">days</div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div class="tw-flex tw-justify-center tw-gap-8">
          <div class="tw-flex tw-flex-col tw-items-center tw-pb-4"></div>
        </div>
        <app-table
          [dataSource]="currentData?.allICDs || []"
          [pageSize]="20"
          [scrollable]="false"
          [expandable]="false"
          dataKey="id"
          [canExpand]="canExpand"
          recordLabel="ICD"
          recordLabelCase="none"
          [canShowFullRow]="showRtwRow"
        >
          <app-column name="position" label="Order" />
          <app-column name="code" label="ICD Code">
            <ng-template #value let-data>
              <div
                class="tw-flex tw-flex-col tw-gap-2  tw-text-[var(--sed-black-80-color)]"
              >
                <div class="tw-text-xl">{{ data.code }}</div>
                <div class="tw-text-sm tw-font-bold">{{ data.group }}</div>
              </div>
            </ng-template>
          </app-column>

          <app-column name="description" label="Description">
            <ng-template #value let-data>
              <div class="tw-text-pretty tw-max-w-96 tw-leading-6 tw-text-md">
                {{ data.description }}
              </div>
            </ng-template>
          </app-column>
          <app-column name="midRangeDays" label="Mid-Range Days" format="n" />
          <app-column name="midRangeDate" label="Mid-Range Date" format="d" />
          <app-column name="atRiskDays" label="At-Risk Days" format="n" />
          <app-column name="atRiskDate" label="At-Risk Date" format="d" />
          @if (showRtwFields) {
            <app-column [fullRow]="true">
              <ng-template #value let-data>
                <app-detail-panel [dataSource]="data">
                  <app-detail-item
                    label="RTW best practice"
                    name="rtwBestPractice"
                    labelCase="none"
                  >
                    <ng-template #value let-data>
                      {{ data.rtwBestPractice }}
                    </ng-template>
                  </app-detail-item>
                  <app-detail-item
                    label="RTW duration"
                    name="rtwDuration"
                    labelCase="none"
                  >
                    <ng-template #value let-data>
                      {{ data.rtwDuration }}
                    </ng-template>
                  </app-detail-item>
                </app-detail-panel>
              </ng-template>
            </app-column>
          }
        </app-table>
      </ng-template>
    </app-loading-panel>
  `,
  styles: `
    .arc {
      --b: 8px;
      width: 60px;
      aspect-ratio: 1;
      padding: var(--b);
      border-radius: 50%;
      --_g: /var(--b) var(--b) no-repeat
        radial-gradient(50% 50%, #000 97%, #0000);
      mask:
        top var(--_g),
        calc(50% + 50% * sin(var(--a))) calc(50% - 50% * cos(var(--a)))
          var(--_g),
        linear-gradient(#0000 0 0) content-box intersect,
        conic-gradient(#000 var(--a), #0000 0);
    }

    .med-gradient {
      background-image: url('/assets/images/med-gradient.png');
      background-size: cover;
    }
  `,
})
export class IcdComponent extends BaseComponent implements OnInit {
  private formatPipe = inject(FormatPipe);

  private static readonly INITIAL_DAYS_OFF_WORK = 0;
  private static readonly DATE_NOT_AVAILABLE = 'N/A';

  currentData: MedicalUIData | null = null;
  allStructuredData: MedicalUIData[] = [];
  effectiveDates: EffectiveDate[] = [];
  effectiveDate = new FormControl<string>('');

  currentClaim: ClaimRow;
  SelectedICDCode = '2';
  medicalResponse: MedicalICD9Response;
  timelineCard: TimelineCard;

  offWorkDays: number = IcdComponent.INITIAL_DAYS_OFF_WORK;
  firstAbsenceDate = '';
  formattedFirstAbsenceDate = '';
  showRtwFields = true; // Hidden for DS/GL claims

  labelDayIndex = 0;

  workStatus = 'Loading...';

  readonly riskCategories = [
    { key: 'low-risk' as const, label: 'Low-risk' },
    { key: 'mid-range' as const, label: 'Mid-range' },
    { key: 'at-risk' as const, label: 'At-risk' },
  ];

  loading = false;
  error: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    this.currentClaim = this.view.currentClaim();

    // Hide RTW fields for DS/GL claims
    this.showRtwFields = !['DS', 'GL'].includes(
      this.currentClaim?.lineOfBusiness,
    );

    const [medicalResponse, timelineCard, timeTracking] = await Promise.all([
      this.api.medical.get({
        recordId: this.currentClaim.recordId,
        recordSource: this.currentClaim.recordSource,
      }),
      this.api.claimSummary.getTimelineCard(
        this.currentClaim.recordSource || 'V1VIEW',
        this.currentClaim.recordId,
      ),
      this.api.timeTracking.get(this.currentClaim),
    ]);

    this.medicalResponse = medicalResponse;
    this.timelineCard = timelineCard;
    this.workStatus = timeTracking?.overview?.status;

    if (
      this.medicalResponse &&
      Array.isArray(this.medicalResponse) &&
      this.medicalResponse.length > 0
    ) {
      // Sort by currentEffectiveDate first, then by icdSeq within each date group
      const sortedResponse = this.medicalResponse.sort((a, b) => {
        const dateComparison =
          new Date(b.currentEffectiveDate).getTime() -
          new Date(a.currentEffectiveDate).getTime();
        if (dateComparison !== 0) return dateComparison;
        return a.icdSeq - b.icdSeq;
      });

      // Group by date for effective date options (remove duplicates)
      const uniqueDates = sortedResponse.filter(
        (record, index, array) =>
          array.findIndex(
            (r) =>
              new Date(r.currentEffectiveDate).getTime() ===
              new Date(record.currentEffectiveDate).getTime(),
          ) === index,
      );

      this.effectiveDates =
        MedicalUIModelFactory.createEffectiveDateOptions(uniqueDates);

      if (this.effectiveDates.length > 0) {
        this.effectiveDate.setValue(this.effectiveDates[0].value);
        // Trigger the initial load with the first effective date
        this.onEffectiveDateChange(this.effectiveDates[0].value);
      }

      this.dispose.subs = this.effectiveDate.valueChanges.subscribe(
        (selectedDate: string) => {
          this.onEffectiveDateChange(selectedDate);
        },
      );
    } else {
      const defaultData = MedicalUIModelFactory.createMedicalUIData({} as any);
      this.currentData = {
        ...defaultData,
        allICDs: [], // Override to empty array to prevent showing empty row
      };
      this.effectiveDates = []; // Empty effective dates dropdown
    }

    this.loadTimeTrackingData();
    this.updateLabels();
  }

  canExpand(row: any): boolean {
    return !!row.des;
  }

  getReturnToWorkDaysByCategory(
    category: ReturnToWorkDay['riskCategory'],
  ): ReturnToWorkDay[] {
    return (
      this.currentData?.returnToWorkDays?.filter(
        (day) => day.riskCategory === category,
      ) ?? []
    );
  }

  onEffectiveDateChange(selectedDate: string): void {
    if (!selectedDate || !this.medicalResponse?.length) {
      const defaultData = MedicalUIModelFactory.createMedicalUIData({} as any);
      this.currentData = {
        ...defaultData,
        allICDs: [], // Override to empty array to prevent showing empty row
      };
      return;
    }

    const recordsForDate = this.getRecordsForDate(selectedDate);
    if (recordsForDate.length > 0) {
      const sortedRecords = this.sortRecordsBySequence(recordsForDate);
      const primaryUIData = this.createPrimaryUIData(sortedRecords[0]);
      const allTableRows = this.buildAllICDTableRows(sortedRecords);

      this.currentData = {
        ...primaryUIData,
        allICDs: allTableRows,
      };
    }

    this.updateLabels();
  }

  protected showRtwRow(name: string, row: any): boolean {
    return row.position === 1;
  }

  private getRecordsForDate(selectedDate: string): MedicalResponse[] {
    return this.medicalResponse.filter((record: MedicalResponse) => {
      const recordDate = this.formatDateToString(record.currentEffectiveDate);
      const selectedDateFormatted = this.formatDateToString(
        new Date(selectedDate),
      );
      return recordDate === selectedDateFormatted;
    });
  }

  private sortRecordsBySequence(records: MedicalResponse[]): MedicalResponse[] {
    return records.sort((a, b) => a.icdSeq - b.icdSeq);
  }

  private createPrimaryUIData(primaryRecord: MedicalResponse): MedicalUIData {
    return MedicalUIModelFactory.createMedicalUIData(primaryRecord);
  }

  private buildAllICDTableRows(sortedRecords: MedicalResponse[]): ICDCode[] {
    const allRows: ICDCode[] = [];

    // Add primary ICD
    const primaryRow = this.createPrimaryICDRow(sortedRecords[0]);
    allRows.push(primaryRow);

    // Collect all unique secondary ICD codes
    const uniqueSecondaryICDs = new Map<string, ICDCode>();

    sortedRecords.forEach((record) => {
      const secondaryRows = this.createSecondaryICDRowsFromRecord(record);
      secondaryRows.forEach((row) => {
        // Use the code as the key to ensure uniqueness
        if (!uniqueSecondaryICDs.has(row.code)) {
          uniqueSecondaryICDs.set(row.code, row);
        }
      });
    });

    // Add all unique secondary ICDs to the result and reassign positions
    const uniqueSecondaryRows = Array.from(uniqueSecondaryICDs.values());
    uniqueSecondaryRows.forEach((row, index) => {
      row.position = index + 2; // Start from position 2 (primary is position 1)
      row.id = index + 2;
    });

    allRows.push(...uniqueSecondaryRows);

    return this.sortRowsByPosition(allRows);
  }

  private createPrimaryICDRow(primaryRecord: MedicalResponse): ICDCode {
    return {
      id: 1,
      code: primaryRecord.primaryICD9 || '',
      group: primaryRecord.group || '',
      description: primaryRecord.description || '',
      isPrimary: true,
      position: 1,
      midRangeDays: primaryRecord.midRangeDays || null,
      midRangeDate: this.formatDateToString(primaryRecord.midRangeDate) || null,
      atRiskDays: primaryRecord.atRiskDays || null,
      atRiskDate: this.formatDateToString(primaryRecord.atRiskDate) || null,
      // RTW fields (only for primary ICD)
      rtwBestPractice: primaryRecord.rtwBestPractice,
      rtwDuration: primaryRecord.rtwDuration,
    };
  }

  private createSecondaryICDRowsFromRecord(record: MedicalResponse): ICDCode[] {
    const secondaryRows: ICDCode[] = [];

    // Check main icdCode
    if (record.icdCode && record.icdCode.trim()) {
      secondaryRows.push({
        id: record.icdSeq,
        code: record.icdCode,
        group: record.icdGroup || '',
        description: record.icdDesc || '',
        isPrimary: false,
        position: record.icdSeq,
        midRangeDays: null,
        midRangeDate: null,
        atRiskDays: null,
        atRiskDate: null,
      });
    }

    // Check icdCode2-5
    for (let i = 2; i <= 5; i++) {
      const codeField = `icdCode${i}` as keyof MedicalResponse;
      const groupField = `icdGroup${i}` as keyof MedicalResponse;
      const descField = `icdDesc${i}` as keyof MedicalResponse;

      const code = record[codeField] as string;
      if (code && code.trim()) {
        secondaryRows.push({
          id: record.icdSeq * 10 + i, // Unique ID
          code: code,
          group: (record[groupField] as string) || '',
          description: (record[descField] as string) || '',
          isPrimary: false,
          position: record.icdSeq * 10 + i,
          midRangeDays: null,
          midRangeDate: null,
          atRiskDays: null,
          atRiskDate: null,
        });
      }
    }

    return secondaryRows;
  }

  private sortRowsByPosition(rows: ICDCode[]): ICDCode[] {
    return rows.sort((a, b) => a.position - b.position);
  }

  private formatDateToString(date: Date | string | null): string | null {
    if (!date) return null;
    return new Date(date).toISOString().split('T')[0];
  }

  private updateLabels() {
    this.labelDayIndex = 0;
    for (
      let index = this.currentData.returnToWorkDays.length - 1;
      index >= 0;
      index--
    ) {
      const element = this.currentData.returnToWorkDays[index];
      if (this.offWorkDays > element.value) {
        this.labelDayIndex = index;
        break;
      }
    }
  }

  private loadTimeTrackingData() {
    if (this.timelineCard) {
      if (
        this.timelineCard.timeTrackingDetails &&
        this.timelineCard.timeTrackingDetails.length > 0
      ) {
        this.offWorkDays = this.calculateOffWorkDays(
          this.timelineCard.timeTrackingDetails,
        );
      } else {
        this.offWorkDays = IcdComponent.INITIAL_DAYS_OFF_WORK;
      }

      if (!this.offWorkDays || this.offWorkDays === 0) {
        this.offWorkDays = IcdComponent.INITIAL_DAYS_OFF_WORK;
      }

      let referenceDate = this.timelineCard.firstAbsence;

      if (!referenceDate) {
        referenceDate =
          this.timelineCard.disabilityDate ||
          this.timelineCard.initDisabilityDate ||
          (this.timelineCard.timeTrackingDetails &&
            this.timelineCard.timeTrackingDetails[0]?.effectiveDate);
      }

      if (referenceDate) {
        this.firstAbsenceDate = referenceDate;
        this.formattedFirstAbsenceDate =
          this.formatPipe.transform(referenceDate, 'd') ||
          IcdComponent.DATE_NOT_AVAILABLE;
      } else {
        this.formattedFirstAbsenceDate = IcdComponent.DATE_NOT_AVAILABLE;
      }
    } else {
      this.offWorkDays = IcdComponent.INITIAL_DAYS_OFF_WORK;
      this.formattedFirstAbsenceDate = IcdComponent.DATE_NOT_AVAILABLE;
    }
  }

  /**
   * Calculate total days off work by summing all "OFF WORK" durations.
   */
  private calculateOffWorkDays(timeTrackingDetails: any[]): number {
    return timeTrackingDetails
      .filter((detail) => detail.status?.toUpperCase().trim() === 'OFF WORK')
      .reduce((sum, detail) => sum + (detail.duration || 0), 0);
  }
}
